import React, { useState, useEffect, useCallback } from 'react';
import Calendar from './components/Calendar';
import WeekView from './components/WeekView';
import DayView from './components/DayView';
import LessonFlow from './components/LessonFlow';
import EventModal from './components/EventModal';
import Header from './components/Header';
import ViewToggle from './components/ViewToggle';
import { Home, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import './App.css';

function App() {
  const [events, setEvents] = useState([]);
  const [keyTimes, setKeyTimes] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day', 'lesson-flow'
  const [showMobileLanding, setShowMobileLanding] = useState(false);
  const [nextEvent, setNextEvent] = useState(null);

  // Check if device is mobile
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  // Get next event from current moment
  const getNextEvent = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Get all events from today onwards
    const upcomingEvents = events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= today;
    });

    if (upcomingEvents.length === 0) return null;

    // Sort by date and time
    upcomingEvents.sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.start_time}`);
      const dateB = new Date(`${b.date} ${b.start_time}`);
      return dateA - dateB;
    });

    // Find the next event from current moment
    const next = upcomingEvents.find(event => {
      const eventDateTime = new Date(`${event.date} ${event.start_time}`);
      return eventDateTime > now;
    });

    return next || upcomingEvents[0];
  }, [events]);

  // Get class colour based on subject
  const getClassColour = (subject) => {
    if (!subject) return '#667eea';
    
    const colours = {
      'mathematics': '#e53e3e',
      'maths': '#e53e3e',
      'english': '#3182ce',
      'science': '#38a169',
      'history': '#d69e2e',
      'geography': '#805ad5',
      'art': '#e53e3e',
      'music': '#d69e2e',
      'pe': '#38a169',
      'computing': '#3182ce',
      'drama': '#805ad5',
      're': '#d69e2e',
      'pshe': '#38a169',
      'french': '#3182ce',
      'spanish': '#e53e3e',
      'german': '#805ad5'
    };

    const subjectLower = subject.toLowerCase();
    for (const [key, colour] of Object.entries(colours)) {
      if (subjectLower.includes(key)) {
        return colour;
      }
    }
    
    return '#667eea'; // Default colour
  };

  // Fetch events from API
  const fetchEvents = async (month, year) => {
    try {
      const response = await fetch(`/api/events?month=${month}&year=${year}`);
      const data = await response.json();
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  // Fetch key times from API
  const fetchKeyTimes = async () => {
    try {
      const response = await fetch('/api/key-times');
      const data = await response.json();
      setKeyTimes(data);
    } catch (error) {
      console.error('Error fetching key times:', error);
    }
  };

  // Save event to API
  const saveEvent = async (eventData) => {
    try {
      const url = selectedEvent ? `/api/events/${selectedEvent.id}` : '/api/events';
      const method = selectedEvent ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      });

      if (response.ok) {
        // Refresh events after saving
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        await fetchEvents(month, year);
        setIsModalOpen(false);
        setSelectedEvent(null);
      } else {
        console.error('Error saving event');
      }
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  // Delete event from API
  const deleteEvent = async (eventId) => {
    try {
      const response = await fetch(`/api/events/${eventId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Refresh events after deletion
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        await fetchEvents(month, year);
        setIsModalOpen(false);
        setSelectedEvent(null);
      } else {
        console.error('Error deleting event');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  // Handle date change
  const handleDateChange = (newDate) => {
    setCurrentDate(newDate);
    const month = newDate.getMonth() + 1;
    const year = newDate.getFullYear();
    fetchEvents(month, year);
  };

  // Handle event click
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  // Handle new event
  const handleNewEvent = (date) => {
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  // Load view mode from localStorage
  useEffect(() => {
    const savedViewMode = localStorage.getItem('teacher-planner-view-mode');
    if (savedViewMode && ['month', 'week', 'day'].includes(savedViewMode)) {
      setViewMode(savedViewMode);
    }
  }, []);

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('teacher-planner-view-mode', viewMode);
  }, [viewMode]);

  // Show mobile landing on first load
  useEffect(() => {
    if (isMobile()) {
      setShowMobileLanding(true);
    }
  }, []);

  // Update next event when events change
  useEffect(() => {
    const next = getNextEvent();
    setNextEvent(next);
  }, [events, getNextEvent]);

  // Load initial data
  useEffect(() => {
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    fetchEvents(month, year);
    fetchKeyTimes();
  }, [currentDate]);

  // Handle view mode change
  const handleViewModeChange = (newViewMode) => {
    setViewMode(newViewMode);
  };

  // Mobile Landing Component
  const MobileLanding = () => {
    if (!showMobileLanding || !isMobile()) return null;

    const next = nextEvent;
    const classColour = next ? getClassColour(next.subject) : '#667eea';

    return (
      <div className="mobile-landing" style={{ backgroundColor: classColour }}>
        <div className="mobile-landing-content">
          {next ? (
            <>
              <div className="mobile-landing-header">
                <h1 className="mobile-landing-title">Next Event</h1>
                <div className="mobile-landing-time">
                  {format(new Date(`${next.date} ${next.start_time}`), 'HH:mm')}
                </div>
              </div>
              
              <div className="mobile-landing-event">
                <h2 className="mobile-landing-event-title">{next.title}</h2>
                {next.subject && (
                  <div className="mobile-landing-subject">{next.subject}</div>
                )}
                {next.room && (
                  <div className="mobile-landing-room">
                    <MapPin size={16} />
                    {next.room}
                  </div>
                )}
                {next.description && (
                  <div className="mobile-landing-description">{next.description}</div>
                )}
              </div>
            </>
          ) : (
            <div className="mobile-landing-no-events">
              <h1 className="mobile-landing-title">No Events</h1>
              <p>No upcoming events scheduled</p>
            </div>
          )}
          
          <button 
            className="mobile-landing-button"
            onClick={() => setShowMobileLanding(false)}
          >
            <Home size={20} />
            Open Planner
          </button>
        </div>
      </div>
    );
  };

  // Render the appropriate view component
  const renderView = () => {
    const commonProps = {
      currentDate,
      events,
      keyTimes,
      onEventClick: handleEventClick,
      onNewEvent: handleNewEvent,
      onDateChange: handleDateChange
    };

    switch (viewMode) {
      case 'week':
        return <WeekView {...commonProps} />;
      case 'day':
        return <DayView {...commonProps} />;
      case 'lesson-flow':
        return <LessonFlow 
          events={events}
          onEventClick={handleEventClick}
          onBack={() => setViewMode('month')}
        />;
      default:
        return <Calendar {...commonProps} />;
    }
  };

  return (
    <div className="app">
      <MobileLanding />
      
      {!showMobileLanding && (
        <>
          <Header 
            currentDate={currentDate}
            onDateChange={handleDateChange}
            viewMode={viewMode}
          />
          <main className="main-content">
            <ViewToggle 
              currentView={viewMode}
              onViewChange={handleViewModeChange}
            />
            {renderView()}
          </main>
        </>
      )}
      
      {isModalOpen && (
        <EventModal
          event={selectedEvent}
          onSave={saveEvent}
          onDelete={deleteEvent}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
}

export default App;